// Change Control types — mirror the backend's
// `ChangeControlRunResponseDto` from
// `packages/nest-backend/src/change_control/dto/change-control.dto.ts`.
// Defined locally to keep consistency with other Quality features that
// inline their types and avoid coupling to Orval's generated wrappers.

export type ChangeControlRunStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'partially_failed'
  | 'failed';

export type ChangeControlRunKind = 'bill_of_operations_release' | 'operation_release';

export interface ChangeControlRunResponse {
  change_control_run_id: string;
  partner_id: string;
  kind: ChangeControlRunKind;
  status: ChangeControlRunStatus;
  bill_of_operations_id: string | null;
  operation_id: string | null;
  target_version: number;
  total: number;
  completed: number;
  failed: number;
  initiated_by: string | null;
  initiated_at: string;
  completed_at: string | null;
  error_summary: string | null;
}

export interface ChangeControlRunItem {
  change_control_run_item_id: string;
  change_control_run_id: string;
  work_order_id: string;
  bill_of_operations_id: string;
  bill_of_operations_version: number;
  status: ChangeControlRunStatus;
  attempts: number;
  error: string | null;
  processed_at: string | null;
}

/**
 * A work order surfaced by the release preview endpoint (`GET
 * /change-control/preview/bill-of-operations`). The response body is
 * undocumented in the OpenAPI spec, so the generated request fn types its
 * `data` as `void`; this is the runtime shape the wizard renders.
 */
export interface AffectedWorkOrder {
  work_order_id: string;
  work_order_name: string;
  /**
   * Short human-readable code from the preview endpoint. Optional because the
   * behind-work-orders grid builds explicit-subset targets from rows that only
   * carry `work_order_name` (no code); the wizard falls back to the name.
   */
  work_order_code?: string;
  bill_of_operations_version: number;
  work_order_current_status: string;
}

/** A status a work order can be protected under — reported by the release preview. */
export type ExcludedWorkOrderStatus = 'in_progress' | 'complete';

export interface ExcludedWorkOrderCount {
  status: ExcludedWorkOrderStatus;
  count: number;
}

/**
 * Response of `GET /change-control/preview/bill-of-operations`: the work
 * orders eligible for release (`affected`) plus a breakdown, by status, of
 * work orders on the same Run Sheet that are protected from it (`excluded`)
 * — backs the Preview tab's "Not Updating" column.
 */
export interface BillOfOperationsReleasePreview {
  affected: AffectedWorkOrder[];
  excluded: ExcludedWorkOrderCount[];
}

export interface StartRunSheetsReleaseInput {
  partner_id: string;
  bill_of_operations_id: string;
  work_order_ids: string[];
}

/** True when the run is in a terminal state (no further progress expected). */
export function isTerminalStatus(status: ChangeControlRunStatus): boolean {
  return status === 'completed' || status === 'partially_failed' || status === 'failed';
}
